import axios, { AxiosResponse } from 'axios';
import qs from 'qs';
import jwt_decode from 'jwt-decode';
import { appConst } from '../AppConst';

export const BASE_URL_API = appConst.URL_BASE.replace(/\/$/, '');
type JwtDecodeResult = {
    exp: number;
};

let refreshTokenRequest: Promise<any> | null = null;

const checkIsTokenExpired = (): boolean => {
    try {
        const accessToken = localStorage.getItem('access_token');
        if (!accessToken) return true;

        // VNPT production may return an opaque access token instead of a JWT.
        // The old logic tried to decode it, treated the failure as "expired",
        // refreshed immediately, then cleared local auth on refresh failure.
        // For non-JWT tokens, skip client-side expiry checks here.
        if (accessToken.split('.').length !== 3) {
            return false;
        }

        const data: JwtDecodeResult = jwt_decode(accessToken);
        return data.exp < Math.floor(Date.now() / 1000);
    } catch (error) {
        console.warn('CHECK_TOKEN_EXPIRED_FALLBACK', error);
        return false;
    }
};

const refreshToken = async () => {
    try {
        const res = await axios.post(`${BASE_URL_API}/api/account/token/refresh`, {
            access_token: localStorage.getItem('access_token'),
            refresh_token: localStorage.getItem('refresh_token')
        });

        if (res.data?.is_success) {
            const { access_token, refresh_token } = res.data.data;
            localStorage.setItem('access_token', access_token);
            localStorage.setItem('refresh_token', refresh_token);
            const auth = JSON.parse(localStorage.getItem('auth') || '{}');
            localStorage.setItem('auth', JSON.stringify({...auth, accessToken: access_token, refreshToken: refresh_token, idToken: res.data.data.id_token || auth.idToken}));
            if (res.data.data.id_token) localStorage.setItem('sso_id_token', res.data.data.id_token);
            return access_token;
        }

        throw new Error('Refresh token failed');
    } catch (error) {
        console.error('REFRESH_FAILED', error);
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        localStorage.removeItem('auth');
        window.location.href = '/login';
        return null;
    }
};

const httpClient = axios.create({
    baseURL: BASE_URL_API,
    timeout: 300000,
    paramsSerializer: (params) => qs.stringify(params, { encode: false })
});

httpClient.interceptors.request.use(
    async (config) => {
        if (!config.headers) {
            config.headers = {} as import('axios').AxiosRequestHeaders;
        }

        const token = localStorage.getItem('access_token');

        const isSsoRequest = config.url?.includes('/api/account/sso/');
        if (token && !isSsoRequest && checkIsTokenExpired()) {
            refreshTokenRequest = refreshTokenRequest || refreshToken();
            const newToken = await refreshTokenRequest;
            refreshTokenRequest = null;

            if (newToken) {
                config.headers['Authorization'] = `Bearer ${newToken}`;
            }
        } else if (token) {
            config.headers['Authorization'] = `Bearer ${token}`;
        }

        config.headers['language'] = localStorage.getItem('language') || 'vi';
        return config;
    },
    (error) => Promise.reject(error)
);

httpClient.interceptors.response.use(
    (response: AxiosResponse) => response,
    (error) => {
        if (error.response?.status === 401) {
            return Promise.resolve({
                status_code: 401,
                is_success: false,
                message: 'Bạn không được phân quyền để thực hiện thao tác này. Vui lòng liên hệ quản trị viên để được hỗ trợ.'
            });
        }

        if (error.response?.status === 403) {
            return Promise.resolve({
                status_code: 403,
                is_success: false,
                message: error.response.data?.message || 'Bạn không có quyền thực hiện thao tác này.',
                data: error.response.data?.data || null
            });
        }

        if (error.response) {
            return Promise.resolve({
                status_code: error.response.status,
                is_success: false,
                message: error.response.data?.message || 'Có lỗi xảy ra',
                data: error.response.data?.data || null
            });
        }

        return Promise.resolve({
            status_code: 500,
            is_success: false,
            message: 'Không thể kết nối đến server',
            data: null
        });
    }
);

export default httpClient;
